import { useEffect, useState } from 'react';
import Select from 'react-select';
import { apiUrl, authHeaders } from '~/services/auth';

// Referral sources ("Referred By") that also record who referred the client:
// every medical source except the center itself. Matched on
// ReferredBySource.code. Mirrors REFERRING_PROFESSIONAL_REFERRAL_CODES in the API.
export const REFERRING_PROFESSIONAL_REFERRAL_CODES = [
    'case_manager',
    'financial',
    'medical_professional',
    'social_worker',
    'tx_coord',
];

export interface ReferringProfessional {
    // Person id of a medical professional at the referring center
    personId: number | null;
    name: string;
    // True when staff type a name for someone not in our system
    isNew: boolean;
}

export const EMPTY_REFERRING_PROFESSIONAL: ReferringProfessional = {
    personId: null,
    name: '',
    isNew: false,
};

export const referringProfessionalFromRequest = (
    campaignRequest: any,
): ReferringProfessional => {
    const professional = campaignRequest?.referring_medical_professional;
    if (professional) {
        return {
            personId: professional.person_id,
            name: professional.name,
            isNew: false,
        };
    }
    if (campaignRequest?.referring_medical_professional_name) {
        return {
            personId: null,
            name: campaignRequest.referring_medical_professional_name,
            isNew: true,
        };
    }
    return EMPTY_REFERRING_PROFESSIONAL;
};

export const referringProfessionalName = (campaignRequest: any): string =>
    campaignRequest?.referring_medical_professional?.name ||
    campaignRequest?.referring_medical_professional_name ||
    '';

export const isReferringProfessionalMissing = (value: ReferringProfessional) =>
    value.isNew ? !value.name.trim() : !value.personId;

export const referringProfessionalPayload = (value: ReferringProfessional) => ({
    referring_medical_professional_person_id: value.isNew
        ? null
        : value.personId,
    referring_medical_professional_name: value.isNew ? value.name.trim() : '',
});

interface Props {
    id: string;
    medicalCenterId: number | null;
    value: ReferringProfessional;
    onChange: (value: ReferringProfessional) => void;
    showError: boolean;
    disabled?: boolean;
    className?: string;
    labelClassName: string;
    inputClassName: string;
    requiredClassName: string;
}

interface ProfessionalOption {
    value: number;
    label: string;
}

const ReferringProfessionalField: React.FC<Props> = ({
    id,
    medicalCenterId,
    value,
    onChange,
    showError,
    disabled,
    className,
    labelClassName,
    inputClassName,
    requiredClassName,
}) => {
    const [options, setOptions] = useState<ProfessionalOption[]>([]);
    const [loading, setLoading] = useState(false);

    // Load the medical professionals at the referring center
    useEffect(() => {
        setOptions([]);
        if (!medicalCenterId) return;

        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const resp = await fetch(
                    `${apiUrl}/v2/searchMedicalProfessionals`,
                    {
                        method: 'POST',
                        headers: authHeaders(),
                        body: JSON.stringify({ medCenterId: medicalCenterId }),
                    },
                );
                if (!resp.ok) {
                    throw new Error('Failed to search medical professionals');
                }
                const people = await resp.json();
                if (!cancelled) {
                    setOptions(
                        people.map((person: any) => ({
                            value: person.id,
                            label: `${person.first_name} ${person.last_name}`,
                        })),
                    );
                }
            } catch (error) {
                console.error('Error loading medical professionals:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [medicalCenterId]);

    return (
        <div className={className}>
            <label htmlFor={id} className={labelClassName}>
                Referring Medical Professional
                <span className={requiredClassName}>*</span>
            </label>
            {value.isNew ? (
                <input
                    id={id}
                    type='text'
                    className={inputClassName}
                    value={value.name}
                    onChange={(e) =>
                        onChange({
                            personId: null,
                            name: e.target.value,
                            isNew: true,
                        })
                    }
                    placeholder='Enter their name'
                    disabled={disabled}
                />
            ) : (
                <Select
                    inputId={id}
                    menuPlacement='auto'
                    className='w-full'
                    options={options}
                    value={
                        value.personId
                            ? { value: value.personId, label: value.name }
                            : null
                    }
                    onChange={(option) =>
                        onChange(
                            option
                                ? {
                                      personId: option.value,
                                      name: option.label,
                                      isNew: false,
                                  }
                                : EMPTY_REFERRING_PROFESSIONAL,
                        )
                    }
                    placeholder='Select a medical professional...'
                    isLoading={loading}
                    isDisabled={disabled || loading}
                    isClearable
                />
            )}
            {showError && (
                <p className='mt-1 text-sm text-[#ff4b4b]'>
                    This field is required
                </p>
            )}
            <p className='mt-2 text-[14px] text-[#666]'>
                {value.isNew
                    ? 'Are they in our system? '
                    : 'Is the person not listed here? '}
                <button
                    type='button'
                    className='text-[#0066CC] underline hover:text-[#0052A3]'
                    onClick={() =>
                        onChange({
                            personId: null,
                            name: '',
                            isNew: !value.isNew,
                        })
                    }
                    disabled={disabled}
                >
                    {value.isNew
                        ? 'Pick them from the list.'
                        : 'Type their name instead.'}
                </button>
            </p>
        </div>
    );
};

export default ReferringProfessionalField;
